/**
 * API functions for managing exporter column mapping profiles
 */

import { supabase } from '../supabase';
import { FieldMapping } from '../../../admin/src/utils/column-matcher';

export interface ColumnMappingProfile {
  id: number;
  exporter_id: number;
  profile_name: string;
  packing_name: string | null;
  sheet_name: string;
  header_row: number;
  mapping_config: FieldMapping; // { "system_field": "excel_column" }
  transforms: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

/**
 * Get all mapping profiles for an exporter
 */
export async function getExporterMappingProfiles(
  exporterId: number
): Promise<ColumnMappingProfile[]> {
  const { data, error } = await supabase
    .from('exporter_column_mappings')
    .select('*')
    .eq('exporter_id', exporterId)
    .order('updated_at', { ascending: false });

  if (error) {
    console.error('Error fetching mapping profiles:', error);
    throw error;
  }

  return data || [];
}

/**
 * Get a specific mapping profile by ID
 */
export async function getMappingProfileById(
  profileId: number
): Promise<ColumnMappingProfile | null> {
  const { data, error } = await supabase
    .from('exporter_column_mappings')
    .select('*')
    .eq('id', profileId)
    .single();

  if (error) {
    console.error('Error fetching mapping profile:', error);
    throw error;
  }

  return data;
}

/**
 * Create a new mapping profile
 */
export async function createMappingProfile(
  exporterId: number,
  profileName: string,
  sheetName: string,
  headerRow: number,
  mappingConfig: FieldMapping,
  packingName?: string,
  transforms?: Record<string, unknown>
): Promise<ColumnMappingProfile> {
  const { data, error } = await supabase
    .from('exporter_column_mappings')
    .insert({
      exporter_id: exporterId,
      profile_name: profileName,
      packing_name: packingName || null,
      sheet_name: sheetName,
      header_row: headerRow,
      mapping_config: mappingConfig,
      transforms: transforms || {},
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating mapping profile:', error);
    throw error;
  }

  return data;
}

/**
 * Update an existing mapping profile
 */
export async function updateMappingProfile(
  profileId: number,
  profileName?: string,
  sheetName?: string,
  headerRow?: number,
  mappingConfig?: FieldMapping,
  packingName?: string,
  transforms?: Record<string, unknown>
): Promise<ColumnMappingProfile> {
  const updates: Partial<ColumnMappingProfile> = {};

  if (profileName !== undefined) {
    updates.profile_name = profileName;
  }

  if (sheetName !== undefined) {
    updates.sheet_name = sheetName;
  }

  if (headerRow !== undefined) {
    updates.header_row = headerRow;
  }

  if (mappingConfig !== undefined) {
    updates.mapping_config = mappingConfig;
  }

  if (packingName !== undefined) {
    updates.packing_name = packingName;
  }

  if (transforms !== undefined) {
    updates.transforms = transforms;
  }

  const { data, error } = await supabase
    .from('exporter_column_mappings')
    .update(updates)
    .eq('id', profileId)
    .select()
    .single();

  if (error) {
    console.error('Error updating mapping profile:', error);
    throw error;
  }

  return data;
}

/**
 * Delete a mapping profile
 */
export async function deleteMappingProfile(profileId: number): Promise<void> {
  const { error } = await supabase
    .from('exporter_column_mappings')
    .delete()
    .eq('id', profileId);

  if (error) {
    console.error('Error deleting mapping profile:', error);
    throw error;
  }
}

/**
 * Check if a profile name already exists for an exporter
 */
export async function profileNameExists(
  exporterId: number,
  profileName: string,
  excludeProfileId?: number
): Promise<boolean> {
  let query = supabase
    .from('exporter_column_mappings')
    .select('id')
    .eq('exporter_id', exporterId)
    .eq('profile_name', profileName);

  if (excludeProfileId) {
    query = query.neq('id', excludeProfileId);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error checking profile name:', error);
    throw error;
  }

  return (data?.length || 0) > 0;
}
